import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';

const IntroScreen = ({ onComplete, onPhaseChange }) => {
  const [phase, setPhase] = useState(1);
  const timerRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    
    // Start at phase 2 immediately (logo + welcome together)
    setPhase(2);
    onPhaseChange?.(2);
    
    // Phase 2 -> 3: Curtains start opening (after 3 seconds)
    const timer2 = setTimeout(() => {
      if (mounted.current) {
        setPhase(3);
        onPhaseChange?.(3);
      }
    }, 3000);

    // Phase 3 -> 4: Almost done
    const timer3 = setTimeout(() => {
      if (mounted.current) {
        setPhase(4);
        onPhaseChange?.(4);
      }
    }, 4600);

    // Complete
    const timer4 = setTimeout(() => {
      if (mounted.current) {
        onComplete();
      }
    }, 5600);

    return () => {
      mounted.current = false;
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []); // Empty deps - run once only

  const isOpening = phase >= 3;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden pointer-events-none">
      {/* Left Curtain Panel */}
      <motion.div
        className="absolute top-0 left-0 w-1/2 h-full z-20"
        style={{
          background: '#030303',
        }}
        animate={{ x: isOpening ? '-100%' : '0%' }}
        transition={{ 
          duration: 1.4, 
          ease: [0.76, 0, 0.24, 1],
        }}
      >
        {/* Neon edge - hidden until curtains open */}
        {isOpening && (
          <motion.div 
            className="absolute top-0 right-0 w-[3px] h-full"
            style={{
              background: 'linear-gradient(180deg, #00F0FF 0%, #FF0099 50%, #FF5F00 100%)',
              boxShadow: '0 0 20px rgba(0, 240, 255, 0.5), 0 0 40px rgba(255, 0, 153, 0.3)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </motion.div>
      
      {/* Right Curtain Panel */}
      <motion.div
        className="absolute top-0 right-0 w-1/2 h-full z-20"
        style={{
          background: '#030303',
        }}
        animate={{ x: isOpening ? '100%' : '0%' }}
        transition={{ 
          duration: 1.4, 
          ease: [0.76, 0, 0.24, 1],
        }}
      >
        {/* Neon edge - hidden until curtains open */}
        {isOpening && (
          <motion.div 
            className="absolute top-0 left-0 w-[3px] h-full"
            style={{
              background: 'linear-gradient(180deg, #FF5F00 0%, #FF0099 50%, #00F0FF 100%)',
              boxShadow: '0 0 20px rgba(255, 95, 0, 0.5), 0 0 40px rgba(255, 0, 153, 0.3)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </motion.div>

      {/* Center content */}
      <motion.div 
        className="relative z-30 flex flex-col items-center px-4"
        animate={isOpening ? { 
          opacity: 0, 
          scale: 0.9,
          y: -30,
        } : {
          opacity: 1,
          scale: 1,
          y: 0,
        }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        {/* Animated gradient orbs behind logo */}
        <motion.div
          className="absolute w-[300px] sm:w-[500px] md:w-[700px] h-[300px] sm:h-[500px] md:h-[700px] rounded-full -z-10"
          style={{
            background: 'radial-gradient(circle, rgba(0, 240, 255, 0.15) 0%, transparent 60%)',
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-[200px] sm:w-[350px] md:w-[500px] h-[200px] sm:h-[350px] md:h-[500px] rounded-full -z-10"
          style={{
            background: 'radial-gradient(circle, rgba(255, 0, 153, 0.15) 0%, transparent 60%)',
          }}
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Logo with glow effect */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="relative"
        >
          {/* Glow behind logo */}
          <motion.div
            className="absolute -inset-8 sm:-inset-12 md:-inset-16 blur-3xl -z-10"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.4) 0%, rgba(255, 0, 153, 0.4) 50%, rgba(255, 95, 0, 0.3) 100%)',
            }}
            animate={{
              opacity: [0.5, 0.8, 0.5],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          
          <img
            src="https://customer-assets.emergentagent.com/job_faith-creators/artifacts/pr4egi4n_Logo%202.png"
            alt="Kingdom V.I.B.E. Studios"
            className="w-56 sm:w-72 md:w-80 lg:w-[420px] xl:w-[500px] relative z-10"
          />
        </motion.div>

        {/* Welcome text - appears with logo */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut', delay: 0.5 }}
          className="mt-6 sm:mt-8 md:mt-12 text-center"
        >
          <p className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-[0.2em] sm:tracking-[0.3em] md:tracking-[0.4em] text-white">
            Welcome
          </p>
          <div className="mt-4 sm:mt-6 flex items-center gap-3 sm:gap-4 md:gap-6">
            <motion.div 
              className="w-8 sm:w-12 md:w-16 h-[1px]"
              style={{
                background: 'linear-gradient(90deg, transparent, #00F0FF)',
              }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            />
            <span className="font-mono text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] md:tracking-[0.3em] text-neon-pink">
              Step Into The Experience
            </span>
            <motion.div 
              className="w-8 sm:w-12 md:w-16 h-[1px]"
              style={{
                background: 'linear-gradient(90deg, #00F0FF, transparent)',
              }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            />
          </div>
        </motion.div>

        {/* Animated gradient line below */}
        <motion.div
          className="mt-6 sm:mt-8 md:mt-12"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="w-24 sm:w-32 md:w-40 h-[2px] bg-gradient-to-r from-neon-blue via-neon-pink to-neon-orange" />
        </motion.div>
      </motion.div>

      {/* Corner accents */}
      <motion.div
        className="absolute top-4 left-4 sm:top-8 sm:left-8 md:top-10 md:left-10 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-l-2 border-t-2 border-neon-blue/40 z-30"
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpening ? 0 : 1 }}
        transition={{ duration: 0.5 }}
      />
      <motion.div
        className="absolute top-4 right-4 sm:top-8 sm:right-8 md:top-10 md:right-10 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-r-2 border-t-2 border-neon-pink/40 z-30"
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpening ? 0 : 1 }}
        transition={{ duration: 0.5 }}
      />
      <motion.div
        className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 md:bottom-10 md:left-10 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-l-2 border-b-2 border-neon-orange/40 z-30"
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpening ? 0 : 1 }}
        transition={{ duration: 0.5 }}
      />
      <motion.div
        className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8 md:bottom-10 md:right-10 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-r-2 border-b-2 border-neon-yellow/40 z-30"
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpening ? 0 : 1 }}
        transition={{ duration: 0.5 }}
      />
    </div>
  );
};

export default IntroScreen;
